
import { controlReasonsOf, primaryControlOf } from 'components/ExportDesign/exportDesignControl';
import { WorkflowProgressStep, WorkflowStepStatus } from 'components/Workflows/StandardWorkflowLayout';

// --- Derives the Export Design Advisor's right-side progress panel from workflow_state.
//
// Unlike EARChatUI/bite-import's fixed phase sequences, Export Design's router dispatches
// per-turn to whichever capability the user actually asked for (classify/ladder/
// licensing/verify/report/converse), in any order — there's no linear "stage index" to
// track. Instead each step is independently marked complete purely from whether the
// relevant data already exists in workflow_state, mirroring EARChatUI's own "fixed steps,
// state-driven completion" pattern. All of it is already round-tripped to the client on
// every turn — no backend changes were needed.
//
// The panel shows the business's 4 named stages (Product Specs & Use / Classify Export /
// Specification Map / Final Design Summary — "Iterate" was dropped by the business), each driven
// by the durable state its capability writes. Intake and intended use are ONE stage; they used to
// be separate steps, because a green "End-use & destination" implied a licensing check that had
// never run (the QA "Spain" finding). Each step stays state-driven rather than a
// strict stage sequence: a step that visibly resets when a spec change invalidates the baseline
// is what keeps the panel honest.
//
// `explain`, `what_if`, `delta`, `converse`, regime ladder and licensing deliberately get no step
// of their own: explain reads prior analysis, what_if overwrites `classification` (so it drives
// the Classify Export step), delta is an on-request design-change capability that is no longer a
// stage, converse produces nothing, and the ladder is built inside the final report and the
// licensing check is on request. A step that can never complete is worse than
// no step.
//
// The panel itself is rendered with disableExpand (see EARChatUI's own
// WorkflowProgressTimeline usage) — a minimal title/status/one-line-detail per step, no
// collapsible drill-down. The underlying product specs / classification data that used to
// live in this panel's expanded details now surfaces as its own chat card widget instead
// (see ExportDesignPhaseCard + the extract* helpers below, used by ExportDesignChatUI).

// The `stage` value streamed on the in-flight frame that, while streaming, means "this
// step is the one currently running". `what_if` joins classify/verify because it re-runs a
// classification over the changed spec sheet rather than producing its own artifact.
//
// Keyed by the step's 1-4 position (Product Specs & Use / Classify Export / Specification Map /
// Final Design Summary). Delta, the regime ladder (built inside the report) and licensing have no
// panel step of their own.
const RUNNING_STAGE_FOR_STEP: Record<number, string[]> = {
  1: ['router.interpret', 'router.facts', 'router.specs'],
  2: ['classify', 'verify', 'what_if'],
  3: ['map_specs'],
  4: ['report'],
};

// "Pending" read as an accusation — as though the user had left their own design unfinished.
// Nothing here is the user's homework: a step is either waiting on information only they can
// give, or it's an analysis that simply hasn't been asked for yet. Saying which also makes
// the optional capabilities discoverable, since nothing else in the UI advertises them.
// Delivered via `value`, which the shared timeline prefers over its own status wording
// (StandardWorkflowLayout's getDefaultStepValue), so no sibling tool's labels change.
const AWAITING_INPUT = 'Awaiting input';
// The note on a complete Product Specs & Use step when no intended use was given.
export const INTENDED_USE_NOT_DISCLOSED = 'Intended use not disclosed';
const ON_REQUEST = 'On request';

const pendingValue = (
  status: WorkflowStepStatus,
  label: string,
): string | undefined => (status === 'pending' ? label : undefined);

type FactLedger = Record<string, { value?: unknown } | undefined>;

const factValue = (facts: FactLedger | undefined, name: string): string | undefined => {
  const value = facts?.[name]?.value;
  if (typeof value === 'string') return value.trim() || undefined;
  return value === undefined || value === null ? undefined : String(value);
};

// Title Case alone mangles what spec keys are made of: acronyms ("Rsa Key Size", "Netd Mk") and a
// trailing unit ("Lens Focal Length Mm", "Frame Rate Hz"). Acronyms are upper-cased, and a unit at
// the end of a key is moved into brackets in its proper form: netd_mk -> "NETD (mK)".
const ACRONYMS = new Set([
  'netd', 'ifov', 'fov', 'rsa', 'ecdh', 'aes', 'tls', 'ssl', 'vpn', 'wan', 'lan', 'gps', 'rf',
  'ir', 'uav', 'usb', 'cpu', 'gpu', 'fpga', 'adc', 'dac', 'mwir', 'lwir', 'swir', 'nir', 'led',
  'lcd', 'api', 'ecc', 'dsp',
]);
const UNIT_SUFFIXES: Record<string, string> = {
  mk: 'mK', hz: 'Hz', khz: 'kHz', mhz: 'MHz', ghz: 'GHz', mm: 'mm', cm: 'cm', m: 'm', km: 'km',
  nm: 'nm', um: '\u00b5m', micron: '\u00b5m', microns: '\u00b5m', mrad: 'mrad', gbps: 'Gbps',
  mbps: 'Mbps', kbps: 'kbps', kg: 'kg', g: 'g', w: 'W', kw: 'kW', mw: 'mW', v: 'V', db: 'dB',
  dbm: 'dBm', c: '\u00b0C', fps: 'fps', rpm: 'rpm', ms: 'ms',
};

const humanizeKey = (key: string): string => {
  const words = key.split('_').filter(Boolean);
  // A lone word is never a unit ("range", not "(range)"): there has to be something it measures.
  const unit = words.length > 1 ? UNIT_SUFFIXES[words[words.length - 1].toLowerCase()] : undefined;
  const label = (unit ? words.slice(0, -1) : words)
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.replace(/\b\w/g, (c) => c.toUpperCase()),
    )
    .join(' ');
  return unit ? `${label} (${unit})` : label;
};

// `matched_spec` is a snake_case key when it points at a spec ("peak_wavelength_nm") but a
// sentence when the classifier describes the product ("Cooled MWIR focal-plane-array camera").
// Title-casing the sentence mangles units and acronyms ("Nm", "Mwir"), so only keys are humanized.
const humanizeSpecRef = (value: string): string =>
  /\s/.test(value.trim()) ? value.trim() : humanizeKey(value.trim());

const statusFor = (
  stepIndex: number,
  complete: boolean,
  currentStage: string | undefined,
  isStreaming: boolean,
): WorkflowStepStatus => {
  if (complete) return 'complete';
  if (isStreaming && currentStage && RUNNING_STAGE_FOR_STEP[stepIndex]?.includes(currentStage)) {
    return 'running';
  }
  return 'pending';
};

export interface BindingConstraintSummary {
  citation?: string;
  matched_spec?: string;
  decontrol_threshold?: string;
  reason_for_control?: string | null;
}

interface Classification {
  result?: {
    current_regime?: string;
    candidate_eccns?: string[];
    candidate_usml?: string[];
    binding_constraints?: BindingConstraintSummary[];
  };
}

interface DesignReportState {
  executive_summary?: string;
  options?: Array<{
    label?: string;
    target_regime?: string;
    change_summary?: string;
    recommended?: boolean;
  }>;
  options_note?: string;
  licensing_summary?: string;
  overall_confidence?: string;
  recommended_next_step?: string;
  caveats?: string[];
}

export interface ProductSpecsSnapshot {
  productName?: string;
  productType?: string;
  productDescription?: string;
  specEntries: Array<[string, string]>;
}

export interface ClassificationSummary {
  currentRegime?: string;
  candidateUsml: string[];
  candidateEccns: string[];
  bindingConstraints: BindingConstraintSummary[];
  /** The USML category (ITAR) or ECCN (EAR-listed) the classification is headlined by. */
  primaryControl?: string;
  /** What that ECCN is controlled for, as codes ("NS", "EI", "AT"), in display order. */
  reasonsForControl: string[];
}

// Shared by the progress panel's single-line `detail` text and the "Product Specs" chat
// card widget, so both always agree on what counts as "intake done".
export const extractProductSpecs = (
  workflowState: Record<string, unknown> | undefined | null,
): ProductSpecsSnapshot => {
  const state = workflowState || {};
  const facts = (state.facts as FactLedger) || {};
  // Same versioned {value, source, version, updated_at} shape as `facts` (see
  // export_design_facts.py's set_spec/_spec_sheet) — not a flat {param: value} map, so
  // each entry needs the same .value unwrap factValue does for facts, not a bare String(v).
  const specSheet = (state.spec_sheet as FactLedger) || {};
  const specEntries = Object.entries(specSheet).map(
    ([key, entry]) => [key, factValue(specSheet, key) ?? String(entry?.value ?? '')] as [string, string],
  );

  return {
    productName: factValue(facts, 'product_name'),
    productType: factValue(facts, 'product_type'),
    productDescription: factValue(facts, 'product_description'),
    specEntries: specEntries,
  };
};

// Shared by the progress panel's single-line `detail` text and the "Key Specs Table" chat
// card widget.
export const extractClassificationSummary = (
  workflowState: Record<string, unknown> | undefined | null,
): ClassificationSummary => {
  const state = workflowState || {};
  const classification = state.classification as Classification | undefined;
  const result = classification?.result;
  return {
    currentRegime: result?.current_regime,
    candidateUsml: result?.candidate_usml || [],
    candidateEccns: result?.candidate_eccns || [],
    bindingConstraints: result?.binding_constraints || [],
    primaryControl: result ? primaryControlOf(result) : undefined,
    reasonsForControl: result ? controlReasonsOf(result) : [],
  };
};

/** The Classify Export step's one line: the control and what it is controlled for first, the regime
 *  as a label after it ("5A002.a.3 · NS, RS, EI, AT — EAR-listed"). "EAR-listed" alone says only that
 *  the item has an ECCN. */
export const classificationDetail = (summary: ClassificationSummary): string => {
  const regime = summary.currentRegime;
  if (!regime) return 'Classification complete';
  if (!summary.primaryControl) return regime;
  const reasons = summary.reasonsForControl.length ? ` · ${summary.reasonsForControl.join(', ')}` : '';
  return `${summary.primaryControl}${reasons} — ${regime}`;
};

export const productSpecsHaveContent = (snapshot: ProductSpecsSnapshot): boolean =>
  Boolean(snapshot.productName || snapshot.productType || snapshot.productDescription) ||
  snapshot.specEntries.length > 0;

export const classificationSummaryHasContent = (summary: ClassificationSummary): boolean =>
  Boolean(summary.currentRegime) ||
  summary.candidateUsml.length > 0 ||
  summary.candidateEccns.length > 0 ||
  summary.bindingConstraints.length > 0;

export { humanizeKey, humanizeSpecRef };

export const buildExportDesignProgressSteps = (
  workflowState: Record<string, unknown> | undefined | null,
  currentStage: string | undefined,
  isStreaming: boolean,
): WorkflowProgressStep[] => {
  const state = workflowState || {};
  const specs = extractProductSpecs(state);
  const intakeDone = productSpecsHaveContent(specs);

  const facts = (state.facts as FactLedger) || {};
  const destinationCountry = factValue(facts, 'destination_country');
  const intendedUse = factValue(facts, 'intended_use');
  // "Product Specs & Use" (stage 1) is complete once the product's specs are given. The intended
  // use helps the classification but is the user's call, so without one the step still completes,
  // with a note that it was not disclosed. The destination country is likewise NOT required: it only
  // lets the tool advise on specific licensing, so it is shown when given and never asked for here.
  const productSpecsAndUseDone = intakeDone;

  const classificationSummary = extractClassificationSummary(state);
  // Require an actual regime, not just a truthy (but possibly empty/degenerate)
  // workflow_state.classification object — the backend's own empty-result guard means this
  // shouldn't happen from its own writes, but workflow_state round-trips through the client
  // every turn, so don't let a malformed/corrupted echo mark this stage complete on nothing.
  const jurisdictionDone = Boolean(classificationSummary.currentRegime);
  const bindingConstraintCount = classificationSummary.bindingConstraints.length;
  // The map is its own, requested stage: done only once the user has been shown it (the backend
  // sets spec_map_shown when it presents the map), not merely because a classification exists.
  const specMapShown = state.spec_map_shown === true;
  const specsTableDone = specMapShown && bindingConstraintCount > 0;
  // Classification can legitimately come back with zero binding constraints (e.g. a clean
  // EAR99 result with nothing controlled to list) — that's a real, final answer, not "still
  // pending". Without this branch the step would sit on "Pending" forever even after the
  // classification finished, which is exactly the kind of vague/confusing state this panel
  // should never show.
  const specsTableSkipped = bindingConstraintCount === 0 && jurisdictionDone;

  const report = state.report as DesignReportState | undefined;
  const reportDone = Boolean(report);


  const productSpecsAndUseStatus = statusFor(1, productSpecsAndUseDone, currentStage, isStreaming);
  const jurisdictionStatus = statusFor(2, jurisdictionDone, currentStage, isStreaming);
  // Specification Map's running state keys off the new, dedicated `map_specs` backend stage
  // (streamed as its own turn right after classify completes) rather than off classify itself.
  const specsTableStatus: WorkflowStepStatus = specsTableSkipped
    ? 'skipped'
    : statusFor(3, specsTableDone, currentStage, isStreaming);
  const reportStatus = statusFor(4, reportDone, currentStage, isStreaming);

  // Every pending/partial branch below names precisely what's missing rather than falling
  // through to the shared timeline's generic "Awaiting information..." default — a step
  // should never leave the user guessing which fact is still needed.
  const steps: WorkflowProgressStep[] = [
    {
      label: 'Product Specs & Use',
      // While the product itself isn't described yet, that's the gap; once it is the step is
      // complete, and the detail notes an intended use that was not disclosed.
      detail: !intakeDone
        ? specs.productName ||
          specs.productType ||
          (specs.specEntries.length > 0
            ? `${specs.specEntries.length} specs captured`
            : 'Describe the product, its key specs and what it is for')
        : !intendedUse
        ? destinationCountry
          ? `${INTENDED_USE_NOT_DISCLOSED} · ${destinationCountry}`
          : INTENDED_USE_NOT_DISCLOSED
        : destinationCountry
        ? `${intendedUse} → ${destinationCountry}`
        : intendedUse,
      status: productSpecsAndUseStatus,
      value: pendingValue(productSpecsAndUseStatus, AWAITING_INPUT),
    },
    {
      label: 'Classify Export',
      detail: jurisdictionDone
        ? classificationDetail(classificationSummary)
        : 'Waiting for a product classification to run',
      status: jurisdictionStatus,
      // Until there's a product to classify this is genuinely on the user; after that the
      // tool can run it whenever asked.
      value: pendingValue(jurisdictionStatus, intakeDone ? ON_REQUEST : AWAITING_INPUT),
    },
    {
      label: 'Specification Map',
      detail: specsTableDone
        ? `${bindingConstraintCount} binding constraint(s) identified`
        : specsTableSkipped
        ? 'No binding constraints identified for this classification'
        : jurisdictionDone
        ? 'Ask which of your specs drove this classification'
        : 'Waiting for classification to identify binding constraints',
      status: specsTableStatus,
      // Before a classification there is nothing to map, so "With classification" is the honest
      // wait; after it, the map is on request. "Skipped" would imply something was bypassed
      // when a clean EAR99 result legitimately has no constraints to list.
      value: specsTableSkipped
        ? 'None found'
        : pendingValue(specsTableStatus, jurisdictionDone ? ON_REQUEST : 'With classification'),
    },
    {
      // Deliberately short and generic, not the executive_summary verbatim — that full text
      // already renders in the report card in the chat itself, and this narrow single-line
      // panel isn't the place to reproduce a multi-sentence paragraph.
      label: 'Final Design Summary',
      detail: reportDone
        ? 'Report generated — see the report card in the chat'
        : 'Ask for a full report to pull everything together',
      status: reportStatus,
      value: pendingValue(reportStatus, ON_REQUEST),
    },
  ];

  return steps;
};

/** A progress line can carry the model's own markdown (a verifier recap such as
 *  "Verdict submitted: **supported = true**"). The live feed is plain text, so drop the markers
 *  and keep the words. */
export const plainProgressText = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
    .trim();
